import { useQuery } from "@apollo/client";
import {
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import type { CallFragment } from "gql/graphql";
import LeftNavCallInvitesActionButton from "./left-nav-call-invites-action-button";
import React, { memo, useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import colors from "app/colors";
import { getUserFullName } from "modules/utils/Functions";
import PhoneInTalkOutlinedIcon from "modules/icons/phone-in-talk-outlined-icon";
import { useUsers } from "modules/providers/users-provider";
import { useVideoCallProvider } from "modules/providers/video-call-provider";
import UserAvatar from "modules/_shared/user-avatar";
import ACTIVE_CALL_LIST_QUERY from "./networking/active-call-list-query";

const LeftNavCallInvites: React.FC = () => {
  const { usersById } = useUsers();
  const { fullScreenVideoCall } = useVideoCallProvider();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data } = useQuery(ACTIVE_CALL_LIST_QUERY, {
    variables: { input: { pageSize: 50 } },
    fetchPolicy: "cache-and-network",
  });

  const calls = useMemo(() => data?.activeCallList ?? [], [data]);

  const label = useMemo(() => {
    const n = calls.length;
    if (n === 0) return "";
    return n === 1 ? "1 Call" : `${n} Calls`;
  }, [calls.length]);

  const joinCall = useCallback(
    (callId: string) => {
      fullScreenVideoCall({ callId });
      onClose();
    },
    [fullScreenVideoCall, onClose],
  );

  const renderCallRow = useCallback(
    (index: number, call: CallFragment) => (
      <Box>
        <HStack w="full" justify="space-between" gap={2} py={3} px={3}>
          <HStack minW={0} flex={1} gap={2}>
            <UserAvatar
              userId={call.createdUserId}
              size="sm"
              displayOnlineIndicator={false}
            />
            <Box minW={0}>
              <Text
                fontSize={14}
                fontWeight={700}
                color={colors.blue}
                noOfLines={1}
              >
                {getUserFullName(usersById[call.createdUserId])}
              </Text>
              <Text fontSize={12} color={colors.gray50} noOfLines={1}>
                {call.isVideo ? "Video call" : "Audio call"}
              </Text>
            </Box>
          </HStack>
          <LeftNavCallInvitesActionButton onClick={() => joinCall(call.id)}>
            Join
          </LeftNavCallInvitesActionButton>
        </HStack>
        {index < calls.length - 1 ? (
          <Box
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor={colors.gray0}
            mx="16px"
          />
        ) : null}
      </Box>
    ),
    [calls.length, joinCall, usersById],
  );

  if (calls.length === 0) {
    return null;
  }

  return (
    <Box w="full" px="4px">
      <HStack
        w="full"
        bg={colors.yellow100}
        py={1}
        pl={3}
        pr={2}
        borderRadius={4}
      >
        <PhoneInTalkOutlinedIcon
          fill={colors.blackTertiary}
          boxSize={4}
          flexShrink={0}
        />
        <Text
          flex={1}
          fontSize={14}
          color={colors.blackTertiary}
          fontWeight={500}
          letterSpacing="-0.28px"
          noOfLines={1}
          lineHeight="short"
        >
          {label}
        </Text>
        {calls.length === 1 ? (
          <LeftNavCallInvitesActionButton onClick={() => joinCall(calls[0].id)}>
            Join
          </LeftNavCallInvitesActionButton>
        ) : (
          <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            placement="right-start"
            closeOnBlur
          >
            <PopoverTrigger>
              <LeftNavCallInvitesActionButton>
                View
              </LeftNavCallInvitesActionButton>
            </PopoverTrigger>
            <PopoverContent
              w="320px"
              maxH="min(420px, 70vh)"
              display="flex"
              flexDirection="column"
              bg={colors.white}
              borderRadius="8px"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={colors.grayPrimary}
              boxShadow="lg"
            >
              <PopoverArrow bg={colors.white} />
              <PopoverHeader
                borderBottomWidth={0}
                color={colors.blackSecondary}
                fontFamily="Outfit"
                fontSize={20}
                fontStyle="normal"
                fontWeight={700}
                lineHeight="normal"
                letterSpacing="-0.6px"
                textAlign="left"
                py={3}
                px={3}
              >
                Call Invites ({calls.length})
              </PopoverHeader>
              <PopoverBody p={0} overflow="hidden">
                <Box h="min(348px, calc(70vh - 72px))" w="full" minH="120px">
                  <Virtuoso<CallFragment>
                    data={calls}
                    style={{ height: "100%", width: "100%" }}
                    defaultItemHeight={81}
                    itemContent={renderCallRow}
                    computeItemKey={(_, call) => call.id}
                  />
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </HStack>
    </Box>
  );
};

export default memo(LeftNavCallInvites);
